class PremiumStoryCarousel extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.viewport = this.querySelector('[data-viewport]');
    this.track = this.querySelector('[data-track]');
    if (!this.viewport || !this.track) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.originals = Array.from(this.track.children);
    this.count = this.originals.length;
    this.index = 0;
    this.physical = this.count > 1 ? this.count : 0;
    this.interval = Math.max(1000, Number(this.dataset.interval) * 1000 || 1000);
    this.duration = Math.min(Number(this.dataset.duration) || 650, this.interval - 100);
    this.style.setProperty('--psc-duration', `${this.duration}ms`);
    this.lastMoveAt = null;
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.paused = this.motion.matches;
    this.hovered = false;
    this.focused = false;
    this.inView = false;
    this.busy = false;
    this.editorSelected = false;
    this.pointer = null;
    if (this.count > 1) {
      const cloneSet = () => this.originals.map((slide) => {
        const clone = slide.cloneNode(true);
        clone.dataset.clone = '';
        for (const element of [clone, ...clone.querySelectorAll('*')]) {
          element.removeAttribute('id');
          for (const attribute of [...element.attributes]) {
            if (attribute.name.startsWith('data-shopify-')) element.removeAttribute(attribute.name);
          }
        }
        return clone;
      });
      this.track.prepend(...cloneSet());
      this.track.append(...cloneSet());
    }
    this.slides = Array.from(this.track.children);
    this.querySelector('[data-controls]')?.removeAttribute('hidden');
    this.addEventListener('click', (event) => {
      if (this.suppressClick && event.target.closest('.psc__slide')) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (event.target.closest('[data-next]')) this.move(1, true);
      if (event.target.closest('[data-previous]')) this.move(-1, true);
      if (event.target.closest('[data-pause]')) {
        this.paused = !this.paused;
        if (!this.paused) {
          // Explicitly starting rotation overrides the current hover/focus pause.
          // Entering or focusing the section again will pause it as usual.
          this.hovered = false;
          this.focused = false;
        }
        this.updatePause();
        this.schedule();
      }
    }, { signal, capture: true });
    this.viewport.addEventListener('keydown', (event) => {
      if (event.target.closest('video') || event.altKey || event.metaKey || event.ctrlKey) return;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        this.viewport.focus({ preventScroll: true });
        this.move(event.key === 'ArrowRight' ? 1 : -1, true);
      }
    }, { signal });
    this.viewport.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary || event.button !== 0 || event.target.closest('video')) return;
      this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY };
      this.suppressClick = false;
      this.schedule();
    }, { signal, passive: true });
    window.addEventListener('pointerup', (event) => {
      if (!this.pointer || event.pointerId !== this.pointer.id) return;
      const dx = event.clientX - this.pointer.x;
      const dy = event.clientY - this.pointer.y;
      this.pointer = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.2) {
        this.suppressClick = true;
        clearTimeout(this.clickTimer);
        this.clickTimer = setTimeout(() => { this.suppressClick = false; }, 400);
        this.move(dx < 0 ? 1 : -1, true);
      }
      this.schedule();
    }, { signal, passive: true });
    window.addEventListener('pointercancel', () => { this.pointer = null; this.schedule(); }, { signal });
    this.viewport.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    this.addEventListener('mouseenter', () => { this.hovered = this.dataset.hover === 'true'; this.schedule(); }, { signal });
    this.addEventListener('mouseleave', () => { this.hovered = false; this.schedule(); }, { signal });
    this.addEventListener('focusin', () => { this.focused = true; this.schedule(); }, { signal });
    this.addEventListener('focusout', (event) => { this.focused = this.contains(event.relatedTarget); this.schedule(); }, { signal });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.pauseVideos();
      this.schedule();
    }, { signal });
    this.motion.addEventListener('change', () => {
      this.paused = this.motion.matches;
      this.updatePause();
      this.schedule();
    }, { signal });
    for (const video of this.querySelectorAll('video')) {
      video.addEventListener('play', () => {
        for (const other of this.querySelectorAll('video')) if (other !== video) other.pause();
        this.schedule();
      }, { signal });
      video.addEventListener('pause', () => this.schedule(), { signal });
      video.addEventListener('ended', () => this.schedule(), { signal });
    }
    this.addEventListener('shopify:block:select', (event) => {
      const id = event.detail?.blockId || event.target.closest('[data-block-id]')?.dataset.blockId;
      const index = this.originals.findIndex((slide) => slide.dataset.blockId === id);
      if (index < 0) return;
      this.editorSelected = true;
      this.finish();
      this.index = index;
      this.physical = (this.base || 0) + index;
      this.layout();
      this.schedule();
    }, { signal });
    this.addEventListener('shopify:block:deselect', () => { this.editorSelected = false; this.schedule(); }, { signal });
    this.resizeObserver = new ResizeObserver(() => {
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => { this.finish(); this.measure(); });
    });
    this.resizeObserver.observe(this.viewport);
    this.intersectionObserver = new IntersectionObserver(([entry]) => {
      this.inView = entry.isIntersecting;
      if (!this.inView) this.pauseVideos();
      this.schedule();
    }, { threshold: 0.1 });
    this.intersectionObserver.observe(this);
    this.track.addEventListener('transitionend', (event) => {
      if (event.target === this.track && event.propertyName === 'transform') this.finish();
    }, { signal });
    this.measure();
    this.classList.add('is-ready');
    this.updatePause();
  }

  measure() {
    const slide = this.slides[0];
    if (!slide) return;
    // Keep subpixel widths: rounding each step drifts the centered card after clones.
    this.width = parseFloat(getComputedStyle(slide).width) || slide.offsetWidth;
    this.gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    this.step = this.width + this.gap;
    this.center = (this.viewport.clientWidth - this.width) / 2;
    // Repeat more sets for very wide screens / very small card counts.
    // Three sets are sufficient only when each set spans half the viewport.
    const needed = Math.ceil((this.viewport.clientWidth / 2 + this.step) / (this.count * (this.width * 0.7 + this.gap)));
    if (this.count > 1 && needed > (this.bufferSets || 1)) {
      const current = this.bufferSets || 1;
      for (let set = current; set < needed; set++) {
        const front = this.slides.slice(0, this.count).map((slide) => slide.cloneNode(true));
        const back = front.map((slide) => slide.cloneNode(true));
        this.track.prepend(...front);
        this.track.append(...back);
      }
      this.bufferSets = needed;
      this.slides = Array.from(this.track.children);
    }
    this.base = this.count > 1 ? this.count * (this.bufferSets || 1) : 0;
    this.physical = this.base + this.index;
    this.layout();
  }

  layout(animate = false) {
    if (!this.step) return;
    this.classList.toggle('is-animated', animate && !this.motion.matches);
    this.track.style.transform = `translate3d(${this.center - this.physical * this.step}px, 0, 0)`;
    const styles = getComputedStyle(this.viewport);
    const centerScale = parseFloat(styles.getPropertyValue('--psc-center-scale')) || 0.88;
    const mobileCurve = parseFloat(styles.getPropertyValue('--psc-mobile-curve')) || 1;
    const growth = (parseFloat(styles.getPropertyValue('--psc-curve-growth')) || 0) * 1.3 * mobileCurve;
    const strength = (Number(this.dataset.tilt) || 0) * 6 * mobileCurve;
    const perspective = Math.max(420, this.width * 4.2);
    const halfSlots = Math.max(1.6, this.viewport.clientWidth / (2 * this.step));
    // Project each card's actual left/right edges, then keep a consistent gap.
    // This creates a concave row: a small, flat center and larger outer cards.
    const geometry = (distance) => {
      const relative = Math.min(1.15, Math.abs(distance) / halfSlots);
      const scale = centerScale + growth * Math.pow(relative, 2.6);
      const yaw = -Math.sign(distance) * Math.min(50, strength * relative);
      const radians = yaw * Math.PI / 180;
      const half = this.width * scale / 2;
      const projection = half * Math.sin(radians) / perspective;
      return {
        scale, yaw,
        left: half * Math.cos(radians) / (1 - projection),
        right: half * Math.cos(radians) / (1 + projection),
        heightScale: scale / (1 - Math.abs(projection)),
      };
    };
    const positions = new Map([[0, { ...geometry(0), center: 0 }]]);
    for (const direction of [-1, 1]) {
      const limit = direction < 0 ? this.physical : this.slides.length - this.physical - 1;
      for (let offset = 1; offset <= limit; offset++) {
        const distance = direction * offset;
        const previous = positions.get(distance - direction);
        const card = geometry(distance);
        card.center = previous.center + direction * (
          this.gap + (direction > 0 ? previous.right + card.left : previous.left + card.right)
        );
        positions.set(distance, card);
      }
    }
    let maximumHeightScale = centerScale;
    this.slides.forEach((slide, physical) => {
      const distance = physical - this.physical;
      const card = positions.get(distance);
      slide.style.setProperty('--psc-x', `${card.center - distance * this.step}px`);
      slide.style.setProperty('--psc-scale', String(card.scale));
      slide.style.setProperty('--psc-yaw', `${card.yaw}deg`);
      slide.style.setProperty('--psc-perspective', `${perspective}px`);
      const x = this.viewport.clientWidth / 2 + card.center;
      const visible = x + card.right > 1 && x - card.left < this.viewport.clientWidth - 1;
      slide.inert = !visible;
      slide.setAttribute('aria-hidden', String(!visible));
      slide.toggleAttribute('data-active', physical === this.physical);
      if (visible) maximumHeightScale = Math.max(maximumHeightScale, card.heightScale);
      else slide.querySelector('video')?.pause();
    });
    const cardHeight = this.slides[0].offsetHeight;
    // Reserve the complete projected height, including the nearest outer corner.
    this.viewport.style.setProperty('--psc-stage-padding', `${Math.ceil(Math.max(0, cardHeight * (maximumHeightScale - 1) / 2) + 16)}px`);
    this.dataset.activeIndex = String(this.index);
    const current = this.querySelector('[data-current]');
    if (current) current.textContent = String(this.index + 1).padStart(2, '0');
  }

  move(direction, announce = false) {
    if (this.count < 2 || this.busy || !this.step) return;
    this.pauseVideos();
    clearTimeout(this.timer);
    this.busy = true;
    this.lastMoveAt = performance.now();
    this.index = (this.index + direction + this.count) % this.count;
    this.physical += direction;
    // Flush the non-animated loop reset before starting the next movement.
    this.track.getBoundingClientRect();
    this.layout(true);
    if (announce) {
      const status = this.querySelector('[data-status]');
      if (status) status.textContent = `Card ${this.index + 1} of ${this.count}`;
    }
    this.finishTimer = setTimeout(() => this.finish(), this.motion.matches ? 0 : this.duration + 80);
  }

  finish() {
    clearTimeout(this.finishTimer);
    this.busy = false;
    if (this.base != null) this.physical = this.base + this.index;
    this.layout();
    this.schedule();
  }

  pauseVideos() {
    this.querySelectorAll('video').forEach((video) => { if (!video.paused) video.pause(); });
  }

  updatePause() {
    const button = this.querySelector('[data-pause]');
    if (!button) return;
    button.setAttribute('aria-pressed', String(this.paused));
    button.setAttribute('aria-label', this.paused ? 'Start automatic sliding' : 'Pause automatic sliding');
    button.querySelector('[data-pause-label]').textContent = this.paused ? 'Play' : 'Pause';
  }

  schedule() {
    clearTimeout(this.timer);
    if (!this.controller || this.count < 2 || this.dataset.autoplay !== 'true' || this.paused || this.hovered || this.focused || this.pointer || this.editorSelected || document.hidden || !this.inView
      || [...this.querySelectorAll('video')].some((video) => !video.paused && !video.ended)) {
      this.lastMoveAt = null;
      return;
    }
    if (this.busy) return;
    // Include animation time in the interval: a one-second setting advances
    // once per second rather than waiting another second after each animation.
    const elapsed = this.lastMoveAt == null ? 0 : performance.now() - this.lastMoveAt;
    this.timer = setTimeout(() => this.move(1), Math.max(0, this.interval - elapsed));
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    clearTimeout(this.timer);
    clearTimeout(this.finishTimer);
    clearTimeout(this.clickTimer);
    cancelAnimationFrame(this.resizeFrame);
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    this.pauseVideos();
    this.querySelectorAll('[data-clone]').forEach((slide) => slide.remove());
    this.bufferSets = 1;
    this.base = null;
    this.classList.remove('is-ready', 'is-animated');
  }
}
if (!customElements.get('premium-story-carousel')) customElements.define('premium-story-carousel', PremiumStoryCarousel);
